import type { components } from '@/lib/api/types.gen';

/** One row of the caps (non-DTF) worklist. */
export type CapLine = components['schemas']['OrderLineView'];

export type CapOrderGroup<T extends CapLine = CapLine> = {
  storeId: string;
  orderId: string;
  orderName: string;
  lines: T[];
};

/** A stable key for one cap line (store + order + line). */
export function capKey(l: Pick<CapLine, 'storeId' | 'orderId' | 'lineId'>): string {
  return `${l.storeId}#${l.orderId}#${l.lineId}`;
}

/**
 * Group caps worklist lines by their order, preserving first-seen order. The worklist arrives
 * oldest-first (FIFO), so a Map keyed by store+order keeps orders (and their lines) in that order.
 * Generic so the fulfill worklist's extended line shape (FulfillLineView, P2b-B) groups without
 * shedding its extra fields; caps callers use the plain CapLine default.
 */
export function groupCapsByOrder<T extends CapLine>(lines: readonly T[]): CapOrderGroup<T>[] {
  const groups = new Map<string, CapOrderGroup<T>>();
  for (const line of lines) {
    const key = `${line.storeId}#${line.orderId}`;
    const g = groups.get(key);
    if (g) {
      g.lines.push(line);
    } else {
      groups.set(key, {
        storeId: line.storeId,
        orderId: line.orderId,
        orderName: line.orderName,
        lines: [line],
      });
    }
  }
  return [...groups.values()];
}
